import { useForm } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Ascend Coach Certification, slice 1 — the admin side of a [[Quiz]]:
// question type, choices, answer key, rationale, and the lesson's quiz
// settings. Beta-gated by the caller (plan decision 19).

export const QUESTION_TYPES = [
  { value: "free_text", label: "Free text (not graded)", badge: null },
  { value: "single_choice", label: "Multiple choice — one answer", badge: "Multiple choice" },
  { value: "multi_select", label: "Multiple choice — select all that apply", badge: "Select all" },
  { value: "true_false", label: "True / False", badge: "True/False" },
]

// Mirrors Question::TRUE_FALSE_CHOICES. A new true/false question has no
// saved choices yet, so the dialog needs its own copy to draw the two radios.
const TRUE_FALSE_CHOICES = [
  { key: "true", text: "True" },
  { key: "false", text: "False" },
]

export function quizFieldsFrom(question) {
  return {
    question_type: question?.questionType || "free_text",
    // True/false choices are fixed and drawn from TRUE_FALSE_CHOICES; keeping
    // them in form state would carry "True"/"False" over as editable text if
    // the admin switches the type.
    choices: question?.questionType === "true_false" ? [] : question?.choices || [],
    correct_keys: question?.correctKeys || [],
    rationale: question?.rationale || "",
  }
}

function nextKey(choices) {
  const used = new Set(choices.map((c) => c.key))
  for (let i = 0; i < 26; i++) {
    const key = String.fromCharCode(97 + i)
    if (!used.has(key)) return key
  }
  return `c${choices.length + 1}`
}

export function QuestionTypeBadge({ type }) {
  const label = QUESTION_TYPES.find((t) => t.value === type)?.badge
  if (!label) return null
  return (
    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
      Quiz · {label}
    </Badge>
  )
}

export function QuizQuestionFields({ data, setData, errors = {} }) {
  const type = data.question_type
  const graded = type !== "free_text"
  const multi = type === "multi_select"
  const choices = type === "true_false" ? TRUE_FALSE_CHOICES : data.choices

  const changeType = (value) => {
    setData((d) => {
      const next = { ...d, question_type: value, correct_keys: [] }
      if (value === "true_false") next.choices = []
      else if (value !== "free_text" && (d.question_type === "true_false" || d.choices.length < 2)) {
        next.choices = [
          { key: "a", text: "" },
          { key: "b", text: "" },
        ]
      }
      return next
    })
  }

  const toggleCorrect = (key) => {
    if (multi) {
      const keys = data.correct_keys.includes(key)
        ? data.correct_keys.filter((k) => k !== key)
        : [...data.correct_keys, key]
      setData("correct_keys", keys)
    } else {
      setData("correct_keys", [key])
    }
  }

  const setChoiceText = (key, text) =>
    setData("choices", data.choices.map((c) => (c.key === key ? { ...c, text } : c)))

  const addChoice = () =>
    setData("choices", [...data.choices, { key: nextKey(data.choices), text: "" }])

  const removeChoice = (key) =>
    setData((d) => ({
      ...d,
      choices: d.choices.filter((c) => c.key !== key),
      correct_keys: d.correct_keys.filter((k) => k !== key),
    }))

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="question-type">Question type</Label>
        <Select value={type} onValueChange={changeType}>
          <SelectTrigger id="question-type" className="w-full bg-white border-zinc-300 text-zinc-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUESTION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.question_type && <p className="text-sm text-red-600">{errors.question_type}</p>}
      </div>

      {graded && (
        <div className="space-y-2">
          <Label>
            Choices{" "}
            <span className="text-xs font-normal text-zinc-500">
              {multi ? "(tick every correct answer)" : "(pick the correct answer)"}
            </span>
          </Label>
          <div className="space-y-2">
            {choices.map((c) => (
              <div key={c.key} className="flex items-center gap-2">
                <input
                  type={multi ? "checkbox" : "radio"}
                  name="correct-choice"
                  aria-label={`Mark "${c.text || c.key}" correct`}
                  className="h-4 w-4 accent-emerald-600 shrink-0"
                  checked={data.correct_keys.includes(c.key)}
                  onChange={() => toggleCorrect(c.key)}
                />
                {type === "true_false" ? (
                  <span className="text-sm text-zinc-800">{c.text}</span>
                ) : (
                  <>
                    <Input
                      className="bg-white border-zinc-300 text-zinc-900"
                      placeholder="Choice text"
                      value={c.text}
                      onChange={(e) => setChoiceText(c.key, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-red-600 shrink-0"
                      disabled={data.choices.length <= 2}
                      onClick={() => removeChoice(c.key)}
                      aria-label="Remove choice"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
          {type !== "true_false" && (
            <Button type="button" variant="outline" size="sm" onClick={addChoice}>
              <Plus className="h-4 w-4 mr-1" />
              Add choice
            </Button>
          )}
          {[errors.choices, errors.correct_keys].filter(Boolean).map((msg) => (
            <p key={msg} className="text-sm text-red-600">{msg}</p>
          ))}
        </div>
      )}

      {graded && (
        <div className="space-y-2">
          <Label htmlFor="question-rationale">Rationale</Label>
          <Textarea
            id="question-rationale"
            placeholder="Why this is the right answer — shown after an attempt."
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            value={data.rationale}
            onChange={(e) => setData("rationale", e.target.value)}
          />
        </div>
      )}
    </>
  )
}

export function QuizSettingsCard({ courseId, lesson }) {
  const form = useForm({
    pass_percent: lesson.passPercent,
    max_attempts: lesson.maxAttempts ?? "",
    reveal_answers: lesson.revealAnswers,
    _redirect: "lesson",
  })

  const submit = (e) => {
    e.preventDefault()
    form.patch(`/app/admin/courses/${courseId}/lessons/${lesson.id}`, { preserveScroll: true })
  }

  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">Quiz settings</CardTitle>
        <p className="text-sm text-zinc-500">
          Used when this lesson has graded questions. The lesson is complete only after a passing attempt.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3 sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="quiz-pass-percent">Pass mark (%)</Label>
            <Input
              id="quiz-pass-percent"
              type="number"
              min="1"
              max="100"
              className="bg-white border-zinc-300 text-zinc-900"
              value={form.data.pass_percent}
              onChange={(e) => form.setData("pass_percent", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="quiz-max-attempts">Attempts allowed</Label>
            <Input
              id="quiz-max-attempts"
              type="number"
              min="1"
              placeholder="Unlimited"
              className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={form.data.max_attempts}
              onChange={(e) => form.setData("max_attempts", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="quiz-reveal-answers"
              checked={form.data.reveal_answers}
              onCheckedChange={(v) => form.setData("reveal_answers", v)}
            />
            <Label htmlFor="quiz-reveal-answers">Show correct answers after an attempt</Label>
          </div>
          <div className="sm:col-span-3">
            <Button type="submit" variant="outline" size="sm" disabled={form.processing}>
              {form.processing ? "Saving..." : "Save quiz settings"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
